import { useCallback } from "react";

type TBaseSessionState = {
  searchParams: string;
};

type TTabbedSessionState = TBaseSessionState & {
  activeTabUrl: string;
};

type TListSessionStorageConfig<T extends TBaseSessionState> = {
  sessionKey: string;
  defaultBackUrl: string;
  getBackUrlFromState?: (state: T) => string;
};

export const useListSessionStorage = <T extends TBaseSessionState>(config: TListSessionStorageConfig<T>) => {
  const { sessionKey, defaultBackUrl, getBackUrlFromState } = config;

  const saveState = useCallback(
    (state: T) => {
      try {
        sessionStorage.setItem(sessionKey, JSON.stringify(state));
      } catch {}
    },
    [sessionKey],
  );

  const getState = useCallback((): T | null => {
    try {
      const stored = sessionStorage.getItem(sessionKey);
      if (!stored) return null;

      const parsed = JSON.parse(stored);

      if (typeof parsed !== "object" || !parsed) return null;
      if (typeof parsed.searchParams !== "string") return null;

      return parsed as T;
    } catch {
      return null;
    }
  }, [sessionKey]);

  const clearState = useCallback(() => {
    try {
      sessionStorage.removeItem(sessionKey);
    } catch {}
  }, [sessionKey]);

  const getBackUrl = useCallback((): string => {
    const state = sessionStorage.getItem(sessionKey);
    if (!state) return defaultBackUrl;

    try {
      const parsed = JSON.parse(state) as T;

      if (getBackUrlFromState) {
        return getBackUrlFromState(parsed);
      }

      const { searchParams } = parsed;
      if (searchParams) {
        return `${defaultBackUrl}?${searchParams}`;
      }
      return defaultBackUrl;
    } catch {
      return defaultBackUrl;
    }
  }, [sessionKey, defaultBackUrl, getBackUrlFromState]);

  return { saveState, getState, clearState, getBackUrl };
};

// Export types for consumers
export type { TBaseSessionState, TTabbedSessionState };
